import type { AnchoredRow, AnchoredRowSceneMetadata } from '../types/anchoredRowTypes'
import type {
  ChapterStructuralRangeLayer,
  StructuralRange,
} from '../types/structuralRangeTypes'
import type { CollationRowTitleSidecarPreview } from './ledgerSafeRowTitles'
import { createStructuralRange } from './structuralRanges'

export interface StructuralRangeMigrationCandidate {
  range: StructuralRange
  sourceRowIds: string[]
}

export interface StructuralRangeMigrationPreview {
  candidates: StructuralRangeMigrationCandidate[]
  skippedSourceRangeIds: string[]
}

const clean = (value: unknown): string => String(value || '').replace(/\s+/g, ' ').trim()

const sceneSourceRangeId = (scene: AnchoredRowSceneMetadata): string => {
  const sceneId = clean(scene.sceneId)
  if (sceneId) return sceneId
  return [
    clean(scene.source),
    Number.isFinite(scene.sceneOrdinal) ? scene.sceneOrdinal : '',
    Number.isFinite(scene.sceneNumber) ? scene.sceneNumber : '',
    Number.isFinite(scene.rowStart) ? scene.rowStart : '',
    Number.isFinite(scene.rowEnd) ? scene.rowEnd : '',
    clean(scene.title),
  ].join('|')
}

const rangeAlreadyImported = (
  layer: ChapterStructuralRangeLayer,
  sourceKind: NonNullable<StructuralRange['provenance']>['sourceKind'],
  sourceRangeId: string,
): boolean => layer.ranges.some(range => (
  range.provenance?.sourceKind === sourceKind
  && range.provenance?.sourceRangeId === sourceRangeId
))

export const previewLegacySceneStructuralRangeMigration = ({
  rows,
  layer,
  now = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  layer: ChapterStructuralRangeLayer
  now?: string
}): StructuralRangeMigrationPreview => {
  const orderedRows = [...rows].sort((left, right) => (
    left.position - right.position
    || left.labelNumber - right.labelNumber
    || left.id.localeCompare(right.id)
  ))
  const groups = new Map<string, { scene: AnchoredRowSceneMetadata; rows: AnchoredRow[] }>()
  orderedRows.forEach(row => {
    const scene = row.metadata?.scene
    if (!scene) return
    const sourceRangeId = sceneSourceRangeId(scene)
    if (!sourceRangeId) return
    const group = groups.get(sourceRangeId)
    if (group) {
      group.rows.push(row)
    } else {
      groups.set(sourceRangeId, { scene, rows: [row] })
    }
  })

  const candidates: StructuralRangeMigrationCandidate[] = []
  const skippedSourceRangeIds: string[] = []
  groups.forEach(({ scene, rows: sourceRows }, sourceRangeId) => {
    const isHtmlSidecar = clean(scene.source) === 'html-sidecar'
      && Boolean(clean(scene.sourceHash))
      && Number.isFinite(scene.sceneOrdinal)
    const provenanceSourceKind = isHtmlSidecar ? 'html-sidecar' as const : 'legacy-row-scene' as const
    const provenanceSourceRangeId = isHtmlSidecar
      ? `${clean(scene.sourceHash)}:${scene.sceneOrdinal}`
      : sourceRangeId
    if (rangeAlreadyImported(layer, provenanceSourceKind, provenanceSourceRangeId)) {
      skippedSourceRangeIds.push(provenanceSourceRangeId)
      return
    }
    const first = sourceRows[0]
    const last = sourceRows[sourceRows.length - 1]
    const title = clean(scene.title) || clean(scene.arabicTitle) || clean(scene.sceneId) || 'Untitled imported range'
    candidates.push({
      range: {
        ...createStructuralRange({
          title,
          kind: null,
          startRowId: first.id,
          endRowId: last.id,
          status: 'unclassified',
          origin: 'migration',
          now,
        }),
        provenance: {
          sourceKind: provenanceSourceKind,
          sourceName: clean(scene.sourceName) || clean(scene.source) || undefined,
          sourceHash: isHtmlSidecar ? clean(scene.sourceHash) : undefined,
          sourceRangeId: provenanceSourceRangeId,
        },
      },
      sourceRowIds: sourceRows.map(row => row.id),
    })
  })

  return { candidates, skippedSourceRangeIds }
}

export const previewSidecarStructuralRangeMigration = ({
  preview,
  layer,
  now = new Date().toISOString(),
}: {
  preview: CollationRowTitleSidecarPreview
  layer: ChapterStructuralRangeLayer
  now?: string
}): StructuralRangeMigrationPreview => {
  const candidates: StructuralRangeMigrationCandidate[] = []
  const skippedSourceRangeIds: string[] = []
  preview.sceneMatches.forEach(match => {
    if (!match.startRowId || !match.endRowId) return
    const sourceRangeId = `${preview.sidecar.source_html_sha256}:${match.sidecarScene.scene_order}`
    if (rangeAlreadyImported(layer, 'html-sidecar', sourceRangeId)) {
      skippedSourceRangeIds.push(sourceRangeId)
      return
    }
    candidates.push({
      range: {
        ...createStructuralRange({
          title: match.sidecarScene.title,
          kind: null,
          startRowId: match.startRowId,
          endRowId: match.endRowId,
          status: 'unclassified',
          origin: 'import',
          now,
        }),
        provenance: {
          sourceKind: 'html-sidecar',
          sourceName: preview.sidecar.source_html_name,
          sourceHash: preview.sidecar.source_html_sha256,
          sourceRangeId,
        },
      },
      sourceRowIds: match.rowIds,
    })
  })
  return { candidates, skippedSourceRangeIds }
}
